import { jwtDecode } from 'jwt-decode';
import { createContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
	keyWiseConfigurationRouteMapping,
	keyWisePayrollSettingsRouteMapping,
	keyWiseSettingsRouteMapping,
} from 'utils/mappings';
import {
	getAllowedModule,
	getAllowedPermissions,
	invalidText,
} from 'utils/utils';
import { fetchProfileAction } from '../../../redux/actions/profileAction';
import { AppDispatch } from '../../../redux/store';
import { setNavigate } from 'apis';
import { fetchCompanyWizard } from '../../../redux/actions/payrollSettingWizardAction';

export const PermissionContext = createContext<any>({
	permissions: null,
	companyId: null,
	userId: null,
});

export const AuthLayout = () => {
	const authpath = [
		'/login',
		'/reset-password',
		'/forgot-password',
		'/set-password',
		'/register',
	];

	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate(); // React Router 6 hook for navigation
	setNavigate(navigate);

	const [permissions, setPermissions] = useState([]);
	const [companyId, setCompanyId] = useState('');
	const [userId, setUserId] = useState('');
	const [isSuperAdmin, setIsSuperAdmin] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const [allowedPermissions, setAllowedPermissions] = useState<any>([]);
	const { pathname } = useLocation();
	const token = localStorage.getItem('accessToken');
	const path = window.location.pathname;

	const fetchProfile = async () => {
		const response = await dispatch(fetchProfileAction());
		if (response.payload?.responseStatus === 401) {
			localStorage.removeItem('accessToken');
			navigate('/login');
		}
		setPermissions(response?.payload?.data?.permissions);

		const _allowedPermissions = getAllowedPermissions(
			response?.payload?.data?.permissions
		);
		setAllowedPermissions(_allowedPermissions);
	};

	useEffect(() => {
		if (!authpath.includes(path) && invalidText(token)) {
			navigate('/login');
		}
	}, [pathname]);

	useEffect(() => {
		if (token) {
			fetchProfile();
			if (path === '/login') {
				navigate('/');
			}
		}
	}, []);

	useEffect(() => {
		if (token) {
			try {
				const userData: User = jwtDecode(token);
				if (userData) {
					setCompanyId(userData?.companyId);
					setUserId(userData?.id);
					setIsSuperAdmin(userData?.isSuperAdmin);
					setIsAdmin(userData?.isAdmin);
				}
			} catch (error: any) {
				localStorage.removeItem('accessToken');
				navigate('/login');
			}
		}
	}, [token]);

	useEffect(() => {
		dispatch(fetchCompanyWizard());
	}, [dispatch]);

	useEffect(() => {
		if (permissions?.length > 0) {
			const splitArray = pathname.split('/');
			const includeSettings = splitArray.includes('settings');
			let key = splitArray[splitArray?.length - 1];

			let allowedConfiguration = getAllowedModule(permissions, [
				'Company',
				'Branch Code',
				'Category Code',
				'Job Title',
				'Pay Point',
				'Departments',
				'Cost Center',
				'Shift Master',
				'Job Grade',
				'Holidays',
				'Leave Type',
			]);

			const allowedPayrollSettings = getAllowedModule(permissions, [
				'Company Setup',
				'Statutory Components',
				'Pay Group',
				'Pay Period',
				'Mid-Month Pay Setup',
				'Deduction Code',
				'Earnings Code',
			]);

			let allowedSettingsModule = getAllowedModule(permissions, [
				'Users',
				'Roles',
			]);

			if (isSuperAdmin) {
				allowedConfiguration = 'Company';
				allowedSettingsModule = 'Users';
			}
			if (
				invalidText(allowedConfiguration) &&
				invalidText(allowedSettingsModule) &&
				invalidText(allowedPayrollSettings) &&
				includeSettings
			) {
				navigate('/');
			} else if (key === 'settings' && allowedSettingsModule) {
					const _key =
						keyWiseSettingsRouteMapping[
							allowedSettingsModule.toLowerCase() as keyof typeof keyWiseSettingsRouteMapping
						];
					navigate(`/settings/${_key}`);
				} else if (key === 'payroll-settings' && allowedConfiguration) {
					const _key =
						keyWiseConfigurationRouteMapping[
							allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
						];
					navigate(`/settings/payroll-settings/${_key}`);
				} else if (key === 'branch-code') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Branch_Code') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/branch-code`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'category-code') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Category_Code') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/category-code`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'pay-point') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Pay_Point') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/pay-point`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'department') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Departments') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/department`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'shift') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Shift_Master') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/shift`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'job-grade') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Job_Grade') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/job-grade`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'job-title') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Job_Title') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/job-title`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'cost-center') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Cost_Center') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/cost-center`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'leave-type') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Leave_Management') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/leave-type`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'holidays') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else if (invalidText(allowedConfiguration)) {
						navigate('/settings');
					} else if (
						allowedPermissions.includes('Allow_Holidays') &&
						allowedConfiguration
					) {
						navigate(`/settings/payroll-settings/holidays`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'company') {
					if (isSuperAdmin) {
						navigate(`/settings/payroll-settings/company`);
					} else {
						const _key =
							keyWiseConfigurationRouteMapping[
								allowedConfiguration.toLowerCase() as keyof typeof keyWiseConfigurationRouteMapping
							];
						navigate(`/settings/payroll-settings/${_key}`);
					}
				} else if (key === 'users' && allowedSettingsModule) {
					if (
						allowedPermissions.includes('Allow_Users')
					) {
						navigate('settings/users');
					} else {
						const _key =
							keyWiseSettingsRouteMapping[
								allowedSettingsModule.toLowerCase() as keyof typeof keyWiseSettingsRouteMapping
							];
						navigate(`/settings/${_key}`);
					}
				} else if (key === 'roles' && allowedSettingsModule) {
					if (
						allowedPermissions.includes('Allow_Roles')
					) {
						navigate('settings/roles');
					} else {
						const _key =
							keyWiseSettingsRouteMapping[
								allowedSettingsModule.toLowerCase() as keyof typeof keyWiseSettingsRouteMapping
							];
						navigate(`/settings/${_key}`);
					}
				} else if (key === 'configuration' && allowedPayrollSettings) {
					const _key =
						keyWisePayrollSettingsRouteMapping[
							allowedPayrollSettings.toLowerCase() as keyof typeof keyWisePayrollSettingsRouteMapping
						];
					navigate(`/settings/configurations/${_key}`);
				} else if (key === 'company-setup') {
					if (
						allowedPermissions.includes('Allow_Company_Setup') &&
						allowedPayrollSettings
					) {
						navigate(`/settings/configurations/company-setup`);
					} else {
						const _key =
							keyWisePayrollSettingsRouteMapping[
								allowedPayrollSettings.toLowerCase() as keyof typeof keyWisePayrollSettingsRouteMapping
							];
						navigate(`/settings/configurations/${_key}`);
					}
				} else if (key === 'pay-period') {
					if (
						allowedPermissions.includes('Allow_Pay_Period') &&
						allowedPayrollSettings
					) {
						navigate(`/settings/configurations/pay-period`);
					} else {
						const _key =
							keyWisePayrollSettingsRouteMapping[
								allowedPayrollSettings.toLowerCase() as keyof typeof keyWisePayrollSettingsRouteMapping
							];
						navigate(`/settings/configurations/${_key}`);
					}
				} else if (key === 'pay-group') {
					if (
						allowedPermissions.includes('Allow_Pay_Group') &&
						allowedPayrollSettings
					) {
						navigate(`/settings/configurations/pay-group`);
					} else {
						const _key =
							keyWisePayrollSettingsRouteMapping[
								allowedPayrollSettings.toLowerCase() as keyof typeof keyWisePayrollSettingsRouteMapping
							];
						navigate(`/settings/configurations/${_key}`);
					}
				} else if (key === 'statutory-components') {
					if (
						allowedPermissions.includes(
							'Allow_Statutory_Components'
						) &&
						allowedPayrollSettings
					) {
						navigate(
							`/settings/configurations/statutory-components`
						);
					} else {
						const _key =
							keyWisePayrollSettingsRouteMapping[
								allowedPayrollSettings.toLowerCase() as keyof typeof keyWisePayrollSettingsRouteMapping
							];
						navigate(`/settings/configurations/${_key}`);
					}
				} else if (key === 'earning-code') {
					if (
						allowedPermissions.includes('Allow_Earnings_Code') &&
						allowedPayrollSettings
					) {
						navigate(`/settings/configurations/earning-code`);
					} else {
						const _key =
							keyWisePayrollSettingsRouteMapping[
								allowedPayrollSettings.toLowerCase() as keyof typeof keyWisePayrollSettingsRouteMapping
							];
						navigate(`/settings/configurations/${_key}`);
					}
				} else if (key === 'deduction-code') {
					if (
						allowedPermissions.includes('Allow_Deduction_Code') &&
						allowedPayrollSettings
					) {
						navigate(`/settings/configurations/deduction-code`);
					} else {
						const _key =
							keyWisePayrollSettingsRouteMapping[
								allowedPayrollSettings.toLowerCase() as keyof typeof keyWisePayrollSettingsRouteMapping
							];
						navigate(`/settings/configurations/${_key}`);
					}
				} else if (key === 'mid-month-pay') {
					if (
						allowedPermissions.includes(
							'Allow_Mid-Month_Pay_Setup'
						) &&
						allowedPayrollSettings
					) {
						navigate(`/settings/configurations/mid-month-pay`);
					} else {
						const _key =
							keyWisePayrollSettingsRouteMapping[
								allowedPayrollSettings.toLowerCase() as keyof typeof keyWisePayrollSettingsRouteMapping
							];
						navigate(`/settings/configurations/${_key}`);
					}
				} else if (key === 'opening-balance') {
					if (
						allowedPermissions.includes('Allow_Opening_Balance') &&
						allowedPayrollSettings
					) {
						navigate(`/settings/configurations/opening-balance`);
					} else {
						const _key =
							keyWisePayrollSettingsRouteMapping[
								allowedPayrollSettings.toLowerCase() as keyof typeof keyWisePayrollSettingsRouteMapping
							];
						navigate(`/settings/configurations/${_key}`);
					}
				} else if (
					key === 'coa' ||
					key === 'employee' ||
					key === 'notification'
				) {
					if (isAdmin) {
						navigate(
							`/settings/${
								key === 'employee' ? 'approval/employee' : key
							}`
						);
					} else {
						const _key =
							keyWiseSettingsRouteMapping[
								allowedSettingsModule.toLowerCase() as keyof typeof keyWiseSettingsRouteMapping
							];
						navigate(`/settings/${_key}`);
					}
				}
			
		}
	}, [permissions, isSuperAdmin]);

	const permissionHandler = (
		_permissions: any,
		companyId: string,
		userId: string,
		isSuperAdmin: boolean,
		isAdmin: boolean,
		isClear?: boolean
	) => {
		setPermissions(_permissions);
		setCompanyId(companyId);
		setUserId(userId);
		setIsSuperAdmin(isSuperAdmin);
		setIsAdmin(isAdmin);
		const _allowedPermissions = getAllowedPermissions(
			isClear ? [] : _permissions
		);
		setAllowedPermissions(_allowedPermissions);
	};

	useEffect(() => {
		if (permissions?.length > 0) {
			setPermissions(permissions);
		}
	}, [permissions]);
	return (
		<PermissionContext.Provider
			value={{
				permissions,
				permissionHandler,
				userId,
				isAdmin,
				companyId,
				isSuperAdmin,
				allowedPermissions,
			}}
		>
			<Outlet />
		</PermissionContext.Provider>
	);
};

interface User {
	email: string;
	exp: number;
	iat: number;
	id: string;
	type: string;
	isAdmin: boolean;
	permissions: any;
	isSuperAdmin: boolean;
	companyId: string;
}
